
import { useState } from 'react';

export default function HomePage() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    vehicle: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const formDataToSend = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        formDataToSend.append(key, value);
      });

      const response = await fetch('https://readdy.ai/api/form/d46o39vjsic9gsa4u7pg', {
        method: 'POST',
        body: formDataToSend
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          vehicle: '',
          service: '',
          message: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-gray-900/95 backdrop-blur-sm border-b border-red-400/20">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-gradient-to-r from-red-500 to-red-700 rounded-full flex items-center justify-center">
                <i className="ri-car-washing-line text-white text-xl"></i>
              </div>
              <h1 className="text-2xl font-bold text-white">
                <span>Bluewhale</span>
                <span className="text-red-400 ml-2">Mobile Detailing</span>
              </h1>
            </div>
            
            <nav className="hidden md:flex items-center space-x-8">
              <button onClick={() => scrollToSection('home')} className="text-white hover:text-red-400 transition-colors cursor-pointer">Home</button>
              <button onClick={() => scrollToSection('about')} className="text-white hover:text-red-400 transition-colors cursor-pointer">About</button>
              <button onClick={() => scrollToSection('services')} className="text-white hover:text-red-400 transition-colors cursor-pointer">Services</button>
              <button onClick={() => scrollToSection('gallery')} className="text-white hover:text-red-400 transition-colors cursor-pointer">Gallery</button>
              <button onClick={() => scrollToSection('testimonials')} className="text-white hover:text-red-400 transition-colors cursor-pointer">Reviews</button>
              <button onClick={() => scrollToSection('contact')} className="text-white hover:text-red-400 transition-colors cursor-pointer">Contact</button>
              <a href="https://calendly.com/detailerx-agencys/mobile-detailing-appointment?month=2025-11" target="_blank" rel="noopener noreferrer" className="font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap bg-gradient-to-r from-red-500 to-red-700 text-white hover:from-red-600 hover:to-red-800 shadow-lg hover:shadow-xl px-4 py-2 text-sm rounded-md">
                Book Now
              </a>
            </nav>
            
            <button 
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden text-white text-2xl cursor-pointer"
            >
              <i className="ri-menu-line"></i>
            </button>
          </div>
        </div>
      </header>

      <main>
        {/* Hero Section */}
        <section id="home" className="relative min-h-screen flex items-center justify-center bg-gray-900 overflow-hidden">
          <div className="container mx-auto px-4 py-20 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
                <span className="text-red-400">Bluewhale</span> Mobile Detailing
                <br />
                <span className="text-3xl md:text-5xl font-light">at Your Doorstep</span>
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-2xl mx-auto leading-relaxed">
                Premium mobile detailing service bringing showroom-level cleaning and protection directly to your location
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <a href="tel:647-584-9042" className="font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap bg-green-500 hover:bg-green-600 text-white px-8 py-4 text-lg rounded-xl transform hover:scale-105 shadow-lg hover:shadow-xl">
                  <i className="ri-phone-line mr-2"></i>Call Now: (647) 584-9042
                </a>
                <button 
                  onClick={() => scrollToSection('services')}
                  className="font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap border-2 border-red-400 text-red-400 hover:bg-red-400 hover:text-white px-8 py-4 text-lg rounded-xl transform hover:scale-105"
                >
                  <i className="ri-eye-line mr-2"></i>View Services
                </button>
              </div>
              
              <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-3xl mx-auto">
                <div className="text-center">
                  <div className="w-16 h-16 bg-red-400/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-map-pin-line text-red-400 text-2xl"></i>
                  </div>
                  <h3 className="text-white font-semibold mb-2">100% Mobile</h3>
                  <p className="text-gray-400 text-sm">We come to your location</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-red-400/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-shield-check-line text-red-400 text-2xl"></i>
                  </div>
                  <h3 className="text-white font-semibold mb-2">Guaranteed</h3>
                  <p className="text-gray-400 text-sm">Satisfaction guaranteed</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-red-400/20 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-award-line text-red-400 text-2xl"></i>
                  </div>
                  <h3 className="text-white font-semibold mb-2">Premium</h3>
                  <p className="text-gray-400 text-sm">Luxury products & equipment</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
            <i className="ri-arrow-down-line text-red-400 text-2xl"></i>
          </div>
        </section>

        {/* About Section */}
        <section id="about" className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                  About <span className="text-red-600">Bluewhale Mobile Detailing</span>
                </h2>
                <div className="w-24 h-1 bg-red-400 mx-auto mb-8"></div>
              </div>
              
              <div className="grid md:grid-cols-2 gap-12 items-center">
                <div>
                  <img 
                    alt="Professional auto detailing service" 
                    className="rounded-2xl shadow-2xl object-cover w-full h-96 object-top" 
                    src="https://public.readdy.ai/ai/img_res/73a43a2c077681cd56b06a64e7fe89ae.jpg"
                  />
                </div>
                <div className="space-y-6">
                  <p className="text-lg text-gray-700 leading-relaxed">
                    At Bluewhale Mobile Detailing, we're committed to delivering exceptional automotive care that exceeds your expectations. Our team of skilled professionals brings years of experience and passion for perfection to every vehicle we service.
                  </p>
                  <p className="text-lg text-gray-700 leading-relaxed">
                    We understand that your vehicle is more than just transportation—it's an investment that deserves the highest level of care. That's why we use only premium products and state-of-the-art equipment to ensure your car receives the premium treatment it deserves.
                  </p>
                  
                  <div className="grid grid-cols-2 gap-6 mt-8">
                    <div className="text-center p-6 bg-white rounded-xl shadow-lg">
                      <div className="text-3xl font-bold text-red-600 mb-2">500+</div>
                      <div className="text-gray-600">Cars Detailed</div>
                    </div>
                    <div className="text-center p-6 bg-white rounded-xl shadow-lg">
                      <div className="text-3xl font-bold text-red-600 mb-2">100%</div>
                      <div className="text-gray-600">Satisfaction Rate</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section id="services" className="py-20 bg-gray-900">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                Our <span className="text-red-400">Services</span>
              </h2>
              <div className="w-24 h-1 bg-red-400 mx-auto mb-8"></div>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                Choose from our range of premium detailing services, all performed at your location
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {/* Regular Wash */}
              <div className="relative bg-gray-800 rounded-2xl p-8 border-2 transition-all duration-300 hover:transform hover:scale-105 border-gray-700 hover:border-red-400/50">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center bg-red-400/20">
                    <i className="ri-car-washing-line text-2xl text-red-400"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">Regular Wash</h3>
                  <div className="text-3xl font-bold text-red-400 mb-4">$149.99</div>
                  <p className="text-gray-400 mb-6">Exterior wash, rim and tire cleaning, and light interior wipe-down</p>
                </div>
                
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center text-gray-300">
                    <i className="ri-check-line text-red-400 mr-3"></i>Exterior wash & dry
                  </li>
                  <li className="flex items-center text-gray-300">
                    <i className="ri-check-line text-red-400 mr-3"></i>Rim & tire cleaning
                  </li>
                  <li className="flex items-center text-gray-300">
                    <i className="ri-check-line text-red-400 mr-3"></i>Light interior wipe-down
                  </li>
                  <li className="flex items-center text-gray-300">
                    <i className="ri-check-line text-red-400 mr-3"></i>Window cleaning
                  </li>
                </ul>
                
                <a href="https://calendly.com/detailerx-agencys/mobile-detailing-appointment?month=2025-11" target="_blank" rel="noopener noreferrer" className="font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap border-2 border-red-400 text-red-400 hover:bg-red-400 hover:text-white px-6 py-3 text-base rounded-lg w-full block text-center">
                  Book Now
                </a>
              </div>

              {/* Interior Only */}
              <div className="relative bg-gray-800 rounded-2xl p-8 border-2 transition-all duration-300 hover:transform hover:scale-105 border-gray-700 hover:border-red-400/50">
                <div className="text-center mb-6">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center bg-red-400/20">
                    <i className="ri-brush-line text-2xl text-red-400"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">Interior Only</h3>
                  <div className="text-3xl font-bold text-red-400 mb-4">Starting at $189.99</div>
                  <p className="text-gray-400 mb-6">Comprehensive interior detailing for all vehicle types</p>
                </div>
                
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center text-gray-300">
                    <i className="ri-check-line text-red-400 mr-3"></i>Sedan - $189.99
                  </li>
                  <li className="flex items-center text-gray-300">
                    <i className="ri-check-line text-red-400 mr-3"></i>SUV 5-seater - $214.99
                  </li>
                  <li className="flex items-center text-gray-300">
                    <i className="ri-check-line text-red-400 mr-3"></i>7-seater & Truck - $229.99
                  </li>
                  <li className="flex items-center text-gray-300">
                    <i className="ri-check-line text-red-400 mr-3"></i>Mini Van - $249.99
                  </li>
                </ul>
                
                <a href="https://calendly.com/detailerx-agencys/mobile-detailing-appointment?month=2025-11" target="_blank" rel="noopener noreferrer" className="font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap border-2 border-red-400 text-red-400 hover:bg-red-400 hover:text-white px-6 py-3 text-base rounded-lg w-full block text-center">
                  Book Now
                </a>
              </div>

              {/* Showroom Package */}
              <div className="relative bg-gray-800 rounded-2xl p-8 border-2 transition-all duration-300 hover:transform hover:scale-105 border-red-400 shadow-2xl shadow-red-400/20">
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-red-400 text-white px-4 py-2 rounded-full text-sm font-bold">Most Popular</span>
                </div>
                
                <div className="text-center mb-6">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center bg-red-400">
                    <i className="ri-star-line text-2xl text-white"></i>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-2">Showroom Package</h3>
                  <div className="text-3xl font-bold text-red-400 mb-4">Starting at $239.99</div>
                  <p className="text-gray-400 mb-6">Complete inside and outside detailing service</p>
                </div>
                
                <ul className="space-y-3 mb-8">
                  <li className="flex items-center text-gray-300">
                    <i className="ri-check-line text-red-400 mr-3"></i>Sedan - $239.99
                  </li>
                  <li className="flex items-center text-gray-300">
                    <i className="ri-check-line text-red-400 mr-3"></i>SUV 5-seater - $264.99
                  </li>
                  <li className="flex items-center text-gray-300">
                    <i className="ri-check-line text-red-400 mr-3"></i>7-seater & Truck - $279.99
                  </li>
                  <li className="flex items-center text-gray-300">
                    <i className="ri-check-line text-red-400 mr-3"></i>Mini Van - $299.99
                  </li>
                </ul>
                
                <a href="https://calendly.com/detailerx-agencys/mobile-detailing-appointment?month=2025-11" target="_blank" rel="noopener noreferrer" className="font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap bg-gradient-to-r from-red-500 to-red-700 text-white hover:from-red-600 hover:to-red-800 shadow-lg hover:shadow-xl px-6 py-3 text-base rounded-lg w-full block text-center">
                  Book Now
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Gallery Section */}
        <section id="gallery" className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Before & <span className="text-red-600">After Gallery</span>
              </h2>
              <div className="w-24 h-1 bg-red-400 mx-auto mb-8"></div>
              <p className="text-xl text-gray-700 max-w-2xl mx-auto">
                See the incredible transformations we achieve with our premium detailing services
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
              {/* Interior Deep Clean */}
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className="p-6">
                  <h3 className="text-xl font-bold text-center mb-6">Interior Deep Clean</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="relative group cursor-pointer">
                      <img 
                        alt="Interior Deep Clean - Before" 
                        className="w-full h-48 object-cover rounded-lg transition-transform duration-300 group-hover:scale-105 object-top" 
                        src="https://static.readdy.ai/image/c99db3a21d90eb43f512347e9168f4f1/ab6c5b2121a4886494f46e369366561c.jfif"
                      />
                      <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="text-white font-bold">View Larger</span>
                      </div>
                      <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 rounded text-sm font-bold">BEFORE</div>
                    </div>
                    <div className="relative group cursor-pointer">
                      <img 
                        alt="Interior Deep Clean - After" 
                        className="w-full h-48 object-cover rounded-lg transition-transform duration-300 group-hover:scale-105 object-top" 
                        src="https://static.readdy.ai/image/c99db3a21d90eb43f512347e9168f4f1/663cd4e0369f800644e1d3dec54c0a1d.jfif"
                      />
                      <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="text-white font-bold">View Larger</span>
                      </div>
                      <div className="absolute top-2 left-2 bg-green-500 text-white px-2 py-1 rounded text-sm font-bold">AFTER</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Exterior Detail */}
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className="p-6">
                  <h3 className="text-xl font-bold text-center mb-6">Exterior Detail</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="relative group cursor-pointer">
                      <img 
                        alt="Exterior Detail - Before" 
                        className="w-full h-48 object-cover rounded-lg transition-transform duration-300 group-hover:scale-105 object-top" 
                        src="https://static.readdy.ai/image/c99db3a21d90eb43f512347e9168f4f1/8dda04267350ea5ad3a40bf6c884281d.jfif"
                      />
                      <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="text-white font-bold">View Larger</span>
                      </div>
                      <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 rounded text-sm font-bold">BEFORE</div>
                    </div>
                    <div className="relative group cursor-pointer">
                      <img 
                        alt="Exterior Detail - After" 
                        className="w-full h-48 object-cover rounded-lg transition-transform duration-300 group-hover:scale-105 object-top" 
                        src="https://public.readdy.ai/ai/img_res/448d8013f2382259e36f4196dbdd5f08.jpg"
                      />
                      <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="text-white font-bold">View Larger</span>
                      </div>
                      <div className="absolute top-2 left-2 bg-green-500 text-white px-2 py-1 rounded text-sm font-bold">AFTER</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* SUV Complete Detail */}
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className="p-6">
                  <h3 className="text-xl font-bold text-center mb-6">SUV Complete Detail</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="relative group cursor-pointer">
                      <img 
                        alt="SUV Complete Detail - Before" 
                        className="w-full h-48 object-cover rounded-lg transition-transform duration-300 group-hover:scale-105 object-top" 
                        src="https://static.readdy.ai/image/c99db3a21d90eb43f512347e9168f4f1/8609aae2015379ebb1d01a8c1fa67a9a.jfif"
                      />
                      <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="text-white font-bold">View Larger</span>
                      </div>
                      <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 rounded text-sm font-bold">BEFORE</div>
                    </div>
                    <div className="relative group cursor-pointer">
                      <img 
                        alt="SUV Complete Detail - After" 
                        className="w-full h-48 object-cover rounded-lg transition-transform duration-300 group-hover:scale-105 object-top" 
                        src="https://static.readdy.ai/image/c99db3a21d90eb43f512347e9168f4f1/6c9a94626ea25cf17cd59900ddceae24.jfif"
                      />
                      <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="text-white font-bold">View Larger</span>
                      </div>
                      <div className="absolute top-2 left-2 bg-green-500 text-white px-2 py-1 rounded text-sm font-bold">AFTER</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Paint Restoration */}
              <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                <div className="p-6">
                  <h3 className="text-xl font-bold text-center mb-6">Paint Restoration</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="relative group cursor-pointer">
                      <img 
                        alt="Paint Restoration - Before" 
                        className="w-full h-48 object-cover rounded-lg transition-transform duration-300 group-hover:scale-105 object-top" 
                        src="https://public.readdy.ai/ai/img_res/cf3b4ab068c12fccabdeeb14061a5ca7.jpg"
                      />
                      <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="text-white font-bold">View Larger</span>
                      </div>
                      <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 rounded text-sm font-bold">BEFORE</div>
                    </div>
                    <div className="relative group cursor-pointer">
                      <img 
                        alt="Paint Restoration - After" 
                        className="w-full h-48 object-cover rounded-lg transition-transform duration-300 group-hover:scale-105 object-top" 
                        src="https://public.readdy.ai/ai/img_res/a60c801ef363b851ffd7dcbfdc62bcec.jpg"
                      />
                      <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <span className="text-white font-bold">View Larger</span>
                      </div>
                      <div className="absolute top-2 left-2 bg-green-500 text-white px-2 py-1 rounded text-sm font-bold">AFTER</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section id="testimonials" className="py-20 bg-gray-900">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
                What Our <span className="text-red-400">Customers Say</span>
              </h2>
              <div className="w-24 h-1 bg-red-400 mx-auto mb-8"></div>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                Don't just take our word for it - hear from our satisfied customers across the GTA
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
              {/* Testimonial 1 */}
              <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700 hover:border-red-400/50 transition-all duration-300 hover:transform hover:scale-105">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-red-400 rounded-full flex items-center justify-center mr-4">
                    <span className="text-white font-bold text-lg">M</span>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold">Michael Chen</h4>
                    <p className="text-gray-400 text-sm">Toronto, ON</p>
                  </div>
                </div>
                
                <div className="flex items-center mb-4">
                  <i className="ri-star-fill text-red-400 text-lg"></i>
                  <i className="ri-star-fill text-red-400 text-lg"></i>
                  <i className="ri-star-fill text-red-400 text-lg"></i>
                  <i className="ri-star-fill text-red-400 text-lg"></i>
                  <i className="ri-star-fill text-red-400 text-lg"></i>
                </div>
                
                <p className="text-gray-300 mb-4 leading-relaxed">
                  "Absolutely incredible service! My BMW looks better than the day I bought it. The team was professional, punctual, and the attention to detail was outstanding. Will definitely be using Bluewhale Mobile Detailing again!"
                </p>
                
                <div className="border-t border-gray-700 pt-4">
                  <p className="text-red-400 text-sm font-semibold">
                    <i className="ri-car-line mr-2"></i>BMW 3 Series
                  </p>
                </div>
              </div>

              {/* Testimonial 2 */}
              <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700 hover:border-red-400/50 transition-all duration-300 hover:transform hover:scale-105">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-red-400 rounded-full flex items-center justify-center mr-4">
                    <span className="text-white font-bold text-lg">S</span>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold">Sarah Johnson</h4>
                    <p className="text-gray-400 text-sm">Mississauga, ON</p>
                  </div>
                </div>
                
                <div className="flex items-center mb-4">
                  <i className="ri-star-fill text-red-400 text-lg"></i>
                  <i className="ri-star-fill text-red-400 text-lg"></i>
                  <i className="ri-star-fill text-red-400 text-lg"></i>
                  <i className="ri-star-fill text-red-400 text-lg"></i>
                  <i className="ri-star-fill text-red-400 text-lg"></i>
                </div>
                
                <p className="text-gray-300 mb-4 leading-relaxed">
                  "Amazing work on my SUV! The interior was completely transformed - it looks and smells brand new. The convenience of having them come to my home was perfect for my busy schedule. Highly recommend!"
                </p>
                
                <div className="border-t border-gray-700 pt-4">
                  <p className="text-red-400 text-sm font-semibold">
                    <i className="ri-car-line mr-2"></i>Honda CR-V
                  </p>
                </div>
              </div>

              {/* Testimonial 3 */}
              <div className="bg-gray-800 rounded-2xl p-6 border border-gray-700 hover:border-red-400/50 transition-all duration-300 hover:transform hover:scale-105">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-red-400 rounded-full flex items-center justify-center mr-4">
                    <span className="text-white font-bold text-lg">D</span>
                  </div>
                  <div>
                    <h4 className="text-white font-semibold">David Rodriguez</h4>
                    <p className="text-gray-400 text-sm">Brampton, ON</p>
                  </div>
                </div>
                
                <div className="flex items-center mb-4">
                  <i className="ri-star-fill text-red-400 text-lg"></i>
                  <i className="ri-star-fill text-red-400 text-lg"></i>
                  <i className="ri-star-fill text-red-400 text-lg"></i>
                  <i className="ri-star-fill text-red-400 text-lg"></i>
                  <i className="ri-star-fill text-red-400 text-lg"></i>
                </div>
                
                <p className="text-gray-300 mb-4 leading-relaxed">
                  "After trying several detailing services, Bluewhale Mobile Detailing stands out from the rest. The ceramic coating they applied looks flawless and protects my Tesla perfectly. Professional, reliable, and worth every penny!"
                </p>
                
                <div className="border-t border-gray-700 pt-4">
                  <p className="text-red-400 text-sm font-semibold">
                    <i className="ri-car-line mr-2"></i>Tesla Model 3
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section id="contact" className="py-20 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Book Your <span className="text-red-600">Appointment</span>
              </h2>
              <div className="w-24 h-1 bg-red-400 mx-auto mb-8"></div>
              <p className="text-xl text-gray-700 max-w-2xl mx-auto mb-8">
                Ready to give your vehicle the premium treatment? Schedule online or call us directly
              </p>
              
              <div className="flex justify-center mb-12">
                <div className="bg-white rounded-full p-1 shadow-lg">
                  <a href="tel:647-584-9042" className="px-8 py-4 rounded-full font-semibold transition-all whitespace-nowrap bg-green-500 text-white hover:bg-green-600 shadow-md mr-2">
                    <i className="ri-phone-line mr-2"></i>Call Now: (647) 584-9042
                  </a>
                  <a 
                    href="https://calendly.com/detailerx-agencys/mobile-detailing-appointment?month=2025-11"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-6 py-3 rounded-full font-semibold transition-all whitespace-nowrap text-gray-600 hover:text-black hover:bg-gray-100"
                  >
                    <i className="ri-calendar-line mr-2"></i>Book Now
                  </a>
                </div>
              </div>
            </div>
            
            <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto">
              {/* Contact Info */}
              <div className="space-y-8">
                <div className="bg-white rounded-2xl p-8 shadow-xl">
                  <h3 className="text-2xl font-bold text-gray-900 mb-6">Get In Touch</h3>
                  <div className="space-y-6">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-red-400 rounded-full flex items-center justify-center mr-4">
                        <i className="ri-phone-line text-white text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Phone</h4>
                        <a href="tel:647-584-9042" className="text-green-600 hover:text-green-700 font-semibold cursor-pointer">
                          (647) 584-9042
                        </a>
                      </div>
                    </div>
                    
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-red-400 rounded-full flex items-center justify-center mr-4">
                        <i className="ri-mail-line text-white text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Email</h4>
                        <a href="mailto:info@bluewhalemobiledetailing.com" className="hover:text-red-400 transition-colors cursor-pointer">
                          info@bluewhalemobiledetailing.com
                        </a>
                      </div>
                    </div>
                    
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-red-400 rounded-full flex items-center justify-center mr-4">
                        <i className="ri-map-pin-line text-white text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Service Area</h4>
                        <p className="text-gray-600">Greater Toronto Area</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-red-400 rounded-full flex items-center justify-center mr-4">
                        <i className="ri-time-line text-white text-xl"></i>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">Hours</h4>
                        <p className="text-gray-600">Mon-Sat: 8AM-6PM<br />Sun: 10AM-4PM</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-8 pt-6 border-t border-gray-200">
                    <a href="tel:647-584-9042" className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-4 px-6 rounded-xl transition-colors cursor-pointer shadow-lg block text-center">
                      <i className="ri-phone-line mr-3 text-xl"></i>Call Now for Instant Booking
                      <div className="text-sm font-normal mt-1">(647) 584-9042</div>
                    </a>
                  </div>
                </div>
                
                <div className="bg-gray-900 rounded-2xl p-8 text-white">
                  <h3 className="text-2xl font-bold mb-4">Why Choose Bluewhale?</h3>
                  <ul className="space-y-3">
                    <li className="flex items-center">
                      <i className="ri-check-line text-red-400 mr-3"></i>100% mobile service - we come to you
                    </li>
                    <li className="flex items-center">
                      <i className="ri-check-line text-red-400 mr-3"></i>Premium-grade products & equipment
                    </li>
                    <li className="flex items-center">
                      <i className="ri-check-line text-red-400 mr-3"></i>Satisfaction guaranteed
                    </li>
                    <li className="flex items-center">
                      <i className="ri-check-line text-red-400 mr-3"></i>Fully insured & bonded
                    </li>
                    <li className="flex items-center">
                      <i className="ri-check-line text-red-400 mr-3"></i>Eco-friendly cleaning solutions
                    </li>
                  </ul>
                </div>
              </div>
              
              {/* Contact Form */}
              <div className="bg-white rounded-2xl p-8 shadow-xl">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">Send Us a Message</h3>
                
                {submitStatus === 'success' && (
                  <div className="mb-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
                    <i className="ri-check-circle-line mr-2"></i>
                    Thank you! Your message has been sent successfully. We'll get back to you soon.
                  </div>
                )}
                
                {submitStatus === 'error' && (
                  <div className="mb-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
                    <i className="ri-error-warning-line mr-2"></i>
                    Sorry, there was an error sending your message. Please try again or call us directly.
                  </div>
                )}
                
                <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                      <input 
                        id="name" 
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required 
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors text-sm" 
                        placeholder="Your full name" 
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">Email Address *</label>
                      <input 
                        id="email" 
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required 
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors text-sm" 
                        placeholder="your.email@example.com" 
                        type="email" 
                      />
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">Phone Number *</label>
                      <input 
                        id="phone" 
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required 
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors text-sm" 
                        placeholder="(647) 123-4567" 
                        type="tel" 
                      />
                    </div>
                    <div>
                      <label htmlFor="vehicle" className="block text-sm font-medium text-gray-700 mb-2">Vehicle Type</label>
                      <input 
                        id="vehicle" 
                        name="vehicle"
                        value={formData.vehicle}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors text-sm" 
                        placeholder="e.g., 2020 Honda Civic" 
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="service" className="block text-sm font-medium text-gray-700 mb-2">Service Interested In</label>
                    <div className="relative">
                      <select 
                        id="service" 
                        name="service"
                        value={formData.service}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 pr-8 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors text-sm appearance-none bg-white"
                      >
                        <option value="">Select a service</option>
                        <option value="Regular Wash">Regular Wash</option>
                        <option value="Deep Clean Interior">Deep Clean Interior</option>
                        <option value="Combo Package">Combo Package</option>
                        <option value="Paint Correction">Paint Correction</option>
                        <option value="Ceramic Coating">Ceramic Coating</option>
                        <option value="Other">Other</option>
                      </select>
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <i className="ri-arrow-down-s-line text-gray-400"></i>
                      </div>
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                    <textarea 
                      id="message" 
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={4} 
                      maxLength={500} 
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors text-sm resize-none" 
                      placeholder="Tell us about your vehicle and any specific requirements..."
                    ></textarea>
                    <p className="text-xs text-gray-500 mt-1">{formData.message.length}/500 characters</p>
                  </div>
                  
                  <button 
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-red-500 to-red-700 text-white font-bold py-4 px-6 rounded-lg hover:from-red-600 hover:to-red-800 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>
                        <i className="ri-loader-4-line mr-2 animate-spin"></i>Sending...
                      </>
                    ) : (
                      <>
                        <i className="ri-send-plane-line mr-2"></i>Send Message
                      </>
                    )}
                  </button>
                </form>
                
                <div className="mt-6 p-4 bg-gray-100 rounded-lg">
                  <h4 className="font-semibold text-gray-800 mb-2">
                    <i className="ri-information-line mr-2 text-red-500"></i>Response Time
                  </h4>
                  <p className="text-sm text-gray-600">
                    We typically respond to all inquiries within 2-4 hours during business hours. For immediate assistance, please call us directly.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div className="md:col-span-2">
              <div className="flex items-center space-x-2 mb-6">
                <div className="w-10 h-10 bg-gradient-to-r from-red-500 to-red-700 rounded-full flex items-center justify-center">
                  <i className="ri-car-washing-line text-white text-xl"></i>
                </div>
                <h3 className="text-2xl font-bold">
                  <span>Bluewhale</span>
                  <span className="text-red-400 ml-2">Mobile Detailing</span>
                </h3>
              </div>
              <p className="text-gray-400 mb-6 max-w-md">
                Premium mobile auto detailing service bringing luxury car care directly to your doorstep. Serving the Greater Toronto Area with premium products and exceptional service.
              </p>
              <div className="flex space-x-4">
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-red-400 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-facebook-fill"></i>
                </a>
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-red-400 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-instagram-line"></i>
                </a>
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-red-400 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-twitter-line"></i>
                </a>
                <a href="#" className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-red-400 hover:text-white transition-colors cursor-pointer">
                  <i className="ri-google-line"></i>
                </a>
              </div>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-6 text-red-400">Quick Links</h4>
              <ul className="space-y-3">
                <li><button onClick={() => scrollToSection('home')} className="text-gray-400 hover:text-red-400 transition-colors cursor-pointer">Home</button></li>
                <li><button onClick={() => scrollToSection('about')} className="text-gray-400 hover:text-red-400 transition-colors cursor-pointer">About Us</button></li>
                <li><button onClick={() => scrollToSection('services')} className="text-gray-400 hover:text-red-400 transition-colors cursor-pointer">Services</button></li>
                <li><button onClick={() => scrollToSection('gallery')} className="text-gray-400 hover:text-red-400 transition-colors cursor-pointer">Gallery</button></li>
                <li><button onClick={() => scrollToSection('contact')} className="text-gray-400 hover:text-red-400 transition-colors cursor-pointer">Contact</button></li>
              </ul>
            </div>
            
            <div>
              <h4 className="text-lg font-semibold mb-6 text-red-400">Contact Info</h4>
              <ul className="space-y-3">
                <li className="flex items-center text-gray-400">
                  <i className="ri-phone-line mr-3 text-red-400"></i>(647) 584-9042
                </li>
                <li className="flex items-center text-gray-400">
                  <i className="ri-mail-line mr-3 text-red-400"></i>
                  <a href="mailto:info@bluewhalemobiledetailing.com" className="hover:text-red-400 transition-colors cursor-pointer">
                    info@bluewhalemobiledetailing.com
                  </a>
                </li>
                <li className="flex items-center text-gray-400">
                  <i className="ri-map-pin-line mr-3 text-red-400"></i>Greater Toronto Area
                </li>
                <li className="flex items-center text-gray-400">
                  <i className="ri-time-line mr-3 text-red-400"></i>Mon-Sat: 8AM-6PM
                </li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-gray-400 text-sm mb-4 md:mb-0">
                © 2025 Bluewhale Mobile Detailing. All rights reserved.
              </p>
              <div className="flex items-center space-x-6">
                <a href="#" className="text-gray-400 hover:text-red-400 text-sm transition-colors cursor-pointer">Privacy Policy</a>
                <a href="#" className="text-gray-400 hover:text-red-400 text-sm transition-colors cursor-pointer">Terms of Service</a>
                <a href="https://readdy.ai/?origin=logo" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-red-400 text-sm transition-colors cursor-pointer">
                  Website Builder
                </a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}